import React from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { selectPosterOpen, useAppStore } from '@runtime/core/store/appStore'
import { useI18n } from '@runtime/core/i18n/i18n'
import { useUser } from '@runtime/core/user/userContext'
import { useUrlThemeSelection } from '@runtime/core/theme/useUrlThemeSelection'
import PosterModal from '@runtime/features/poster/PosterModal'
import ProfileModal from '@runtime/features/profile/ProfileModal'
import appPackage from '../../package.json'

import StudioAdminGuard from './StudioAdminGuard'
import StudioHomeRedirect from './StudioHomeRedirect'
import StudioLoginRoute from './StudioLoginRoute'
import StudioRouteRedirect from './StudioRouteRedirect'

const { lazy, Suspense, useEffect } = React

const BusinessPresentationRoute = lazy(
  () => import('@studio/features/business-presentations/BusinessPresentationRoute'),
)
const HubAdminRoute = lazy(() => import('@studio/routes/hub/admin/HubAdminRoute'))
const HubAdminQrRoute = lazy(() => import('@studio/routes/hub/admin/HubAdminQrRoute'))
const HubAdminBoardGeneratorRoute = lazy(
  () => import('@studio/routes/hub/admin/HubAdminBoardGeneratorRoute'),
)
const HubAdminPrivateBoardsRoute = lazy(
  () => import('@studio/routes/hub/admin/HubAdminPrivateBoardsRoute'),
)
const HubAdminEditorialProjectsRoute = lazy(
  () => import('@studio/routes/hub/admin/HubAdminEditorialProjectsRoute'),
)
const HubAdminProductionBoardRoute = lazy(
  () => import('@studio/routes/hub/admin/HubAdminProductionBoardRoute'),
)
const HubAdminCatalogCollectionsRoute = lazy(
  () => import('@studio/routes/hub/admin/HubAdminCatalogCollectionsRoute'),
)
const HubAdminCommercialPresentationRoute = lazy(
  () => import('@studio/routes/hub/admin/HubAdminCommercialPresentationRoute'),
)

export default function App() {
  const location = useLocation()
  const isBusinessPresentation = location.pathname === '/business/presentation'
  const { loginOpen } = useUser()
  const { t } = useI18n()
  const posterOpen = useAppStore(selectPosterOpen)
  const setPosterOpen = useAppStore(state => state.setPosterOpen)

  useUrlThemeSelection()

  useEffect(() => {
    window.openPoster = () => setPosterOpen(true)

    return () => {
      try {
        delete window.openPoster
      } catch {
        window.openPoster = undefined
      }
    }
  }, [setPosterOpen])

  const withSuspense = (node: React.ReactNode) => <Suspense fallback={null}>{node}</Suspense>

  return (
    <div className="app-shell">
      <div className="app-body">
        <Routes>
          <Route
            path="/business/presentation"
            element={withSuspense(<BusinessPresentationRoute />)}
          />
          <Route
            path="/"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminRoute />
              </StudioAdminGuard>,
            )}
          />
          <Route path="/hub" element={<StudioHomeRedirect />} />
          <Route path="/login" element={<StudioLoginRoute />} />
          <Route path="/access-denied" element={<Navigate to="/login" replace />} />

          <Route
            path="/qrcode"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminQrRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route
            path="/board-generator"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminBoardGeneratorRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route
            path="/boards"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminPrivateBoardsRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route
            path="/editorial-projects"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminEditorialProjectsRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route
            path="/production"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminProductionBoardRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route path="/production-board" element={<StudioRouteRedirect to="/production" />} />

          <Route
            path="/catalog-collections"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminCatalogCollectionsRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route
            path="/hub/admin/commercial-presentation"
            element={withSuspense(
              <StudioAdminGuard>
                <HubAdminCommercialPresentationRoute />
              </StudioAdminGuard>,
            )}
          />

          <Route path="/hub/admin" element={<StudioHomeRedirect />} />
          <Route path="/hub/admin/qrcode" element={<StudioRouteRedirect to="/qrcode" />} />
          <Route
            path="/hub/admin/board-generator"
            element={<StudioRouteRedirect to="/board-generator" />}
          />
          <Route
            path="/hub/admin/editorial-projects"
            element={<StudioRouteRedirect to="/editorial-projects" />}
          />
          <Route
            path="/hub/admin/production-board"
            element={<StudioRouteRedirect to="/production" />}
          />
          <Route
            path="/hub/admin/catalog-collections"
            element={<StudioRouteRedirect to="/catalog-collections" />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {!isBusinessPresentation ? (
          <>
            <PosterModal open={posterOpen} onClose={() => setPosterOpen(false)} />
            <ProfileModal open={loginOpen} />
          </>
        ) : null}
      </div>

      <div className="app-version" aria-live="polite">
        <span>{t('hub.admin.title')}</span>
        <span>v{appPackage.version}</span>
      </div>
    </div>
  )
}
